/**
 * `locationTabTitle` (DNKADM-0230) — the browser tab title derived from the
 * **selected service location**.
 *
 * The shell ships a static `<title>Dink Admin</title>` in `index.html` (the
 * pre-hydration default); once a location is selected the tab identifies it at a
 * glance, which matters with several tenant/location tabs open.
 *
 * The transform is pinned: take the location name, keep only the part **before
 * the first `-`**, trim it, and append `" Dink Admin"` — so
 * `"Sim Creek - Cedar Park"` reads `"Sim Creek Dink Admin"` and
 * `"A - B - C"` reads `"A Dink Admin"`. A name with no `-` is used whole.
 * With no location, no name, or an empty base (e.g. a name that is just `"-"`),
 * the title falls back to the existing default `"Dink Admin"` — never a
 * dangling dash or an empty base.
 *
 * The base is backend-sourced text rendered to the user, so it goes through
 * {@link scrubBrand} at this render boundary like every other backend-copy seam
 * (white-label rule, DNKADM-0044). Pure and React-free — the `document.title`
 * sync lives in `LocationTabTitle`.
 */
import { scrubBrand } from './whitelabel';

/** The static default the app ships with (matches `index.html`'s `<title>`). */
const DEFAULT_TAB_TITLE = 'Dink Admin';

/**
 * Format the browser tab title for a selected location `name` (which may be
 * `null`/`undefined` when nothing is selected). Returns `"Dink Admin"` for
 * the no-location / empty-base cases.
 */
export function locationTabTitle(name: string | null | undefined): string {
  if (name === null || name === undefined) return DEFAULT_TAB_TITLE;
  const base = (name.split('-')[0] ?? '').trim();
  if (base === '') return DEFAULT_TAB_TITLE;
  return `${scrubBrand(base) ?? base} ${DEFAULT_TAB_TITLE}`;
}
